import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { Spinner } from 'components/UI';

import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';

import type { WeeklyBreakdownReport, WeekDayKey, WeekValues } from 'model/BookingsWeeklyBreakdownReport';
import styles from './WeeklyBreakdownTable.module.scss';

type DayKey = WeekDayKey | 'total';
type RowType = 'count' | 'percent' | 'countPercent' | 'currency';

interface RowCountPercentCell {
    value: number;
    pct?: number;
}

type RowCell = number | RowCountPercentCell;

interface WeeklyRow {
    label: string;
    type: RowType;
    values: Record<DayKey, RowCell>;
}

interface WeeklyBreakdownTableProps {
    loading: boolean;
    report: WeeklyBreakdownReport;
}

const DAY_KEYS: WeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const fmtCurrency = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

const fmtPct = (n: number) => `${n}%`;

const buildWeekValuesRow = (v: WeekValues): Record<DayKey, number> => ({
    sun: v.sun,
    mon: v.mon,
    tue: v.tue,
    wed: v.wed,
    thu: v.thu,
    fri: v.fri,
    sat: v.sat,
    total: v.total
});

const buildCountPercentRow = (row: WeeklyBreakdownReport['completed']): Record<DayKey, RowCell> => ({
    sun: row.values.sun,
    mon: row.values.mon,
    tue: row.values.tue,
    wed: row.values.wed,
    thu: row.values.thu,
    fri: row.values.fri,
    sat: row.values.sat,
    total: row.total
});

export default function WeeklyBreakdownTable(props: WeeklyBreakdownTableProps) {
    const { report, loading } = props;

    const daysHeader = useMemo(() => {
        return DAY_KEYS.map(k => {
            const label = report?.days?.[k].format('ddd, [[]MM/DD/YYYY[]]');
            return { key: k, label };
        });
    }, [report]);

    const rows: WeeklyRow[] = useMemo(() => {
        return [
            {
                label: 'Total Bookings',
                type: 'count',
                values: buildWeekValuesRow(report.totalBookings)
            },
            {
                label: '% of Weekly Bookings',
                type: 'percent',
                values: buildWeekValuesRow(report.bookingsPct)
            },
            {
                label: 'Completed (%)',
                type: 'countPercent',
                values: buildCountPercentRow(report.completed)
            },
            {
                label: 'Canceled (%)',
                type: 'countPercent',
                values: buildCountPercentRow(report.canceled)
            },
            {
                label: 'No-Show (%)',
                type: 'countPercent',
                values: buildCountPercentRow(report.noShow)
            },
            {
                label: 'Total Pets',
                type: 'count',
                values: buildWeekValuesRow(report.totalPets)
            },
            {
                label: 'Total Clients',
                type: 'count',
                values: buildWeekValuesRow(report.totalClients)
            },
            {
                label: 'Net Sales',
                type: 'currency',
                values: buildWeekValuesRow(report.netSales)
            },
            {
                label: '% of Sales',
                type: 'percent',
                values: buildWeekValuesRow(report.salesPct)
            }
        ];
    }, [report]);

    return (
        <Table
            firstColumnFixed
            fullHeight
        >
            <TableHeader>
                <TableHeaderCell style={{ minWidth: 220 }} />
                {daysHeader.map(d => (
                    <TableHeaderCell
                        key={d.key}
                        style={{ minWidth: 120 }}
                    >
                        {d.label}
                    </TableHeaderCell>
                ))}
                <TableHeaderCell style={{ minWidth: 140 }}>Weekly Total</TableHeaderCell>
            </TableHeader>

            <TableBody>
                {loading ? (
                    <TableRow>
                        <TableCell
                            colSpan={DAY_KEYS.length + 2}
                            className={styles.loadingCell}
                        >
                            <Spinner className={styles.spinner} />
                        </TableCell>
                    </TableRow>
                ) : (
                    rows.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell className={styles['cell-emphasis']}>{row.label}</TableCell>

                            {DAY_KEYS.map(k => (
                                <TableCell key={k}>{renderCell(row.type, row.values[k])}</TableCell>
                            ))}

                            <TableCell className={styles['total-col']}>
                                {renderCell(row.type, row.values.total, true)}
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
    );
}

function renderCell(type: RowType, cell: RowCell, isTotal = false) {
    if (type === 'currency') return fmtCurrency(cell as number);
    if (type === 'percent') return fmtPct(cell as number);

    if (type === 'countPercent') {
        // total viene como number (row.total)
        if (typeof cell === 'number') return cell;

        const cp = cell as RowCountPercentCell;
        return cp.pct != null && !isTotal ? (
            <>
                {cp.value} ({fmtPct(cp.pct)})
            </>
        ) : (
            cp.value
        );
    }

    // count
    return cell as number;
}
